import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { ROLLOUT_TEXT_TO_SPEECH_VIEWING } from 'constants/featureFlag';
import {
  selectPaused,
  selectPlayerTime,
  selectVideoDuration,
} from 'selectors/videoPlayer';

import { useFeatureFlagValue } from 'hooks/featureFlag';

import styles from './styles.module.less';

const getSpeechUtter = () => {
  const msg = new SpeechSynthesisUtterance();
  const voices = window.speechSynthesis.getVoices();
  // msg.volume = 1; // From 0 to 1

  // Use this page to test voices
  // https://mdn.github.io/web-speech-api/speak-easy-synthesis/
  const voice = voices.find(voice => voice.name === 'Samantha') || voices[0];

  msg.voice = voice;

  msg.rate = 1; // From 0.1 to 10
  msg.pitch = 1.2; // From 0 to 2

  return msg;
};

const speakText = text => {
  window.speechSynthesis.cancel();

  const speechUtter = getSpeechUtter();

  speechUtter.text = text;

  window.speechSynthesis.speak(speechUtter);
};

const useTextToSpeechHook = () => {
  const textBlocks = useSelector(state => state.textOverlay.textBlocks);
  const speechItems = useRef([]);
  const playTime = useSelector(selectPlayerTime);
  const paused = useSelector(selectPaused);

  useEffect(() => {
    // We need to call this early as the voices may load too late when
    // calling .speak
    window.speechSynthesis.getVoices();
  }, []);

  useEffect(() => {
    speechItems.current = textBlocks.map(() => {
      return { speaking: false };
    });
  }, [textBlocks.length]);

  textBlocks.forEach((block, idx) => {
    if (!speechItems.current.length || paused) {
      return;
    }

    const speechItem = speechItems.current[idx];

    if (!speechItem) {
      return;
    }

    if (playTime > block.startTs && playTime < block.endTs) {
      if (!speechItem.speaking) {
        speechItem.speaking = true;
        speakText(block.text);
      }
    } else {
      speechItem.speaking = false;
    }
  });
};

const TextOverlayPlayBarWrapper = () => {
  const text2SpeechVisible = useSelector(state => state.textOverlay.show);
  const textBlocks = useSelector(state => state.textOverlay.textBlocks);
  const textViewingEnabled = useFeatureFlagValue(
    ROLLOUT_TEXT_TO_SPEECH_VIEWING
  );

  if (
    !textViewingEnabled ||
    !text2SpeechVisible ||
    !textBlocks ||
    !textBlocks.length
  ) {
    return null;
  }

  return <TextOverlayPlayBar />;
};

const TextOverlayPlayBar = () => {
  const videoDuration = useSelector(selectVideoDuration);
  const textBlocks = useSelector(state => state.textOverlay.textBlocks);

  useTextToSpeechHook();

  return (
    <div className={styles.container}>
      {textBlocks.map((block, idx) => {
        const startPercentage = (block.startTs / videoDuration) * 100;
        const endPercentage = (block.endTs / videoDuration) * 100;

        const diffPercentage = endPercentage - startPercentage;

        const style = {
          width: `${diffPercentage}%`,
          left: `${startPercentage}%`,
          position: 'absolute',
          backgroundColor: 'var(--lns-color-success)',
          height: '100%',
        };

        return <div style={style} key={idx}></div>;
      })}
    </div>
  );
};

export default TextOverlayPlayBarWrapper;
